/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_BOOKING: 'transition/request-booking',
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_BOOKING_AFTER_INQUIRY: 'transition/request-booking-after-inquiry',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  EXPIRE_PAYMENT: 'transition/expire-payment',
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  ACCEPT_AFTER_PAYMENT: 'transition/accept-after-payment',
  ACCEPT_AFTER_PAYMENT_WITHOUT_VERIFICATION: 'transition/accept-after-payment-without-verification',
  ACCEPT_WITHOUT_VERIFICATION: 'transition/accept-without-verification',
  DECLINE: 'transition/decline',
  DECLINE_AFTER_PAYMENT: 'transition/decline-after-payment',
  CANCEL_CUSTOMER: 'transition/cancel-customer',
  CANCEL_AFTER_PAYMENT: 'transition/cancel-after-payment',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_ACCEPT_AFTER_PAYMENT: 'transition/operator-accept-after-payment',
  OPERATOR_DECLINE: 'transition/operator-decline',
  OPERATOR_DECLINE_AFTER_PAYMENT: 'transition/operator-decline-after-payment',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',
  EXPIRE_AFTER_PAYMENT: 'transition/expire-after-payment',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',

  MARK_PENDING_PAYMENT: 'transition/mark-pending-payment',

  AUTO_PAYMENT: 'transition/auto-payment',

  INITIATE_MANNUAL_PAYMENT: 'transition/initiate-manual-payment',

  CONFIRM_MANNUAL_PAYMENT: 'transition/confirm-manual-payment',

  EXPIRE_MANUAL_PAYMENT: 'transition/expire-manual-payment',

  CANCEL_FROM_PENDING_CONFIRMATION: 'transition/cancel-from-pending-confirmation',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  COMPLETE_AFTER_PAYMENT: 'transition/complete-after-payment',
  OPERATOR_COMPLETE: 'transition/operator-complete',
  OPERATOR_COMPLETE_AFTER_PAYMENT: 'transition/operator-complete-after-payment',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PREAUTHORIZED: 'preauthorized',
  PENDING_REQUEST_PAYMENT: 'pending-request-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED_AFTER_PAYMENT: 'preauthorized-after-payment',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  ACCEPTED_AFTER_PAYMENT: 'accepted-after-payment',
  DECLINED_AFTER_PAYMENT: 'declined-after-payment',
  EXPIRED_AFTER_PAYMENT: 'expired-after-payment',
  PENDING_PAYMENT: 'pending-payment',
  PENDING_CONFIRMATION: 'pending-confirmation',
  MANUAL_PAYMENT_EXPIRED: 'manual-payment-expired',
  PAID: 'paid',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  CANCELED_CUSTOMER: 'cancelled-customer',
  CANCELED_AFTER_PAYMENT: 'cancelled-after-payment',
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_BOOKING]: states.PREAUTHORIZED,
        [transitions.REQUEST_PAYMENT]: states.PENDING_REQUEST_PAYMENT,
      },
    },

    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_BOOKING_AFTER_INQUIRY]: states.PREAUTHORIZED,
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_REQUEST_PAYMENT,
      },
    },

    [states.PREAUTHORIZED]: {
      on: {
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.ACCEPT_WITHOUT_VERIFICATION]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.CANCEL_CUSTOMER]: states.CANCELED_CUSTOMER,
      },
    },

    [states.PENDING_REQUEST_PAYMENT]: {
      on: {
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED_AFTER_PAYMENT,
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED
      }
    },


    [states.ACCEPTED]: {
      on: {
        [transitions.MARK_PENDING_PAYMENT]: states.PENDING_PAYMENT,
      },
    },

    [states.DECLINED]: {},

    [states.EXPIRED]: {},

    [states.PREAUTHORIZED_AFTER_PAYMENT]: {
      on: {
        [transitions.ACCEPT_AFTER_PAYMENT]: states.ACCEPTED_AFTER_PAYMENT,
        [transitions.ACCEPT_AFTER_PAYMENT_WITHOUT_VERIFICATION]: states.ACCEPTED_AFTER_PAYMENT,
        [transitions.OPERATOR_ACCEPT_AFTER_PAYMENT]: states.ACCEPTED_AFTER_PAYMENT,
        [transitions.DECLINE_AFTER_PAYMENT]: states.DECLINED_AFTER_PAYMENT,
        [transitions.OPERATOR_DECLINE_AFTER_PAYMENT]: states.DECLINED_AFTER_PAYMENT,
        [transitions.EXPIRE_AFTER_PAYMENT]: states.EXPIRED_AFTER_PAYMENT,
        [transitions.CANCEL_AFTER_PAYMENT]: states.CANCELED_AFTER_PAYMENT,
      }
    },

    [states.PAYMENT_EXPIRED]: {},

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.AUTO_PAYMENT]: states.PAID,
        [transitions.INITIATE_MANNUAL_PAYMENT]: states.PENDING_CONFIRMATION,
      },
    },

    [states.ACCEPTED_AFTER_PAYMENT]: {
      on: {
        [transitions.COMPLETE_AFTER_PAYMENT]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE_AFTER_PAYMENT]: states.DELIVERED
      }
    },

    [states.DECLINED_AFTER_PAYMENT]: {},
    [states.EXPIRED_AFTER_PAYMENT]: {},



    [states.PENDING_CONFIRMATION]: {
      on: {
        [transitions.CONFIRM_MANNUAL_PAYMENT]: states.PAID,
        [transitions.EXPIRE_MANUAL_PAYMENT]: states.MANUAL_PAYMENT_EXPIRED,
        [transitions.CANCEL_FROM_PENDING_CONFIRMATION]: states.CANCELED,
      },
    },

    [states.PAID]: {
      on: {
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
        [transitions.CANCEL]: states.CANCELED,
      },
    },

    [states.MANUAL_PAYMENT_EXPIRED]: {},

    [states.CANCELED]: {},
    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.ACCEPT_AFTER_PAYMENT,
    transitions.ACCEPT_AFTER_PAYMENT_WITHOUT_VERIFICATION,
    transitions.ACCEPT_WITHOUT_VERIFICATION,
    transitions.OPERATOR_ACCEPT_AFTER_PAYMENT,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.CANCEL_CUSTOMER,
    transitions.CANCEL_AFTER_PAYMENT,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.COMPLETE_AFTER_PAYMENT,
    transitions.OPERATOR_COMPLETE_AFTER_PAYMENT,
    transitions.CONFIRM_MANNUAL_PAYMENT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.DECLINE_AFTER_PAYMENT,
    transitions.OPERATOR_DECLINE_AFTER_PAYMENT,
    transitions.EXPIRE,
    transitions.EXPIRE_AFTER_PAYMENT,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [transitions.REQUEST_BOOKING, transitions.REQUEST_BOOKING_AFTER_INQUIRY, transitions.REQUEST_PAYMENT, transitions.REQUEST_PAYMENT_AFTER_INQUIRY].includes(
    transition
  );
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.COMPLETE_AFTER_PAYMENT,
    transitions.OPERATOR_COMPLETE_AFTER_PAYMENT,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE,
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE_AFTER_PAYMENT,
    transitions.EXPIRE_MANUAL_PAYMENT,
    transitions.CANCEL,
    transitions.CANCEL_FROM_PENDING_CONFIRMATION,
    transitions.DECLINE,
    transitions.DECLINE_AFTER_PAYMENT
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [
  // states.INQUIRY,
  states.PREAUTHORIZED,
  states.PREAUTHORIZED_AFTER_PAYMENT,
  states.DELIVERED,
  states.REVIEWED_BY_CUSTOMER,
];


export const statesNeedingCustomerAttention = [
  states.MANUAL_PAYMENT_EXPIRED,
  states.DELIVERED,
  states.REVIEWED_BY_PROVIDER,
];